import { Avatar, Badge, Button, Drawer, Dropdown, Grid, Layout, Menu, Segmented, Space, Typography } from "antd";
import {
  AlertOutlined,
  AuditOutlined,
  BarChartOutlined,
  CalendarOutlined,
  CheckSquareOutlined,
  DashboardOutlined,
  LogoutOutlined,
  SafetyCertificateOutlined,
  MenuOutlined,
  SettingOutlined,
  ShopOutlined,
} from "@ant-design/icons";
import { useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import type { ActionDto } from "@audit-plan/shared";
import { useAuth } from "../auth/AuthContext";
import { setLang } from "../i18n";
import { api } from "../api/client";
import { palette } from "../theme";
import { GlobalSearch } from "./GlobalSearch";
import { FeedbackShell } from "./FeedbackShell";

const { Sider, Header, Content } = Layout;

export function AppLayout() {
  const { t, i18n } = useTranslation();
  const { me, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  // Menu laterale comprimibile (solo icone); su schermi stretti si chiude del tutto. Stato ricordato nel browser.
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem("ap.nav") === "1";
    } catch {
      return false;
    }
  });
  // Sotto lg (992 px) il menu diventa un pannello a scomparsa aperto dall'hamburger nell'header.
  const screens = Grid.useBreakpoint();
  const narrow = screens.lg === false;
  const [drawer, setDrawer] = useState(false);
  const toggle = (c: boolean) => {
    setCollapsed(c);
    try {
      localStorage.setItem("ap.nav", c ? "1" : "0");
    } catch {
      /* storage non disponibile */
    }
  };
  const overdue = useQuery({
    queryKey: ["actions", { overdue: true }],
    queryFn: () => api.get<{ items: ActionDto[]; total: number }>("/actions", { overdue: true }),
    enabled: !!me,
  });
  if (!me) return null;
  const od = overdue.data?.total ?? 0;
  const isSup = me.role === "supplier";
  const items = isSup
    ? [
        { key: `/suppliers/${me.supplierId}`, icon: <ShopOutlined />, label: t("myCompany") },
        { key: "/actions", icon: <CheckSquareOutlined />, label: <Badge count={od} size="small" offset={[10, 0]} style={{ boxShadow: "none" }} className="nav-badge">{t("myActions")}</Badge> },
        { key: "/audits", icon: <AuditOutlined />, label: t("audits") },
      ]
    : [
        { key: "/", icon: <DashboardOutlined />, label: t("dashboard") },
        { key: "/plan", icon: <CalendarOutlined />, label: t("plan") },
        { key: "/suppliers", icon: <ShopOutlined />, label: t("suppliers") },
        { key: "/audits", icon: <AuditOutlined />, label: t("audits") },
        { key: "/ncs", icon: <AlertOutlined />, label: t("ncs") },
        { key: "/actions", icon: <CheckSquareOutlined />, label: <Badge count={od} size="small" offset={[10, 0]} style={{ boxShadow: "none" }} className="nav-badge">{t("nav_actions")}</Badge>, title: t("actions") },
        { key: "/reports", icon: <BarChartOutlined />, label: t("reports") },
        ...(me.role === "admin" ? [{ key: "/admin", icon: <SettingOutlined />, label: t("admin") }] : []),
      ];
  const sel = items.map((i) => i.key).filter((k) => (k === "/" ? loc.pathname === "/" : loc.pathname.startsWith(k))).sort((a, b) => b.length - a.length)[0];

  const brand = (small: boolean) => (
    <div style={{ display: "flex", gap: 10, alignItems: "center", padding: small ? "18px 19px" : "18px 16px", color: "#fff", whiteSpace: "nowrap", overflow: "hidden" }}>
      <div style={{ flex: "0 0 34px", height: 34, borderRadius: 9, background: palette.primary, display: "grid", placeItems: "center", fontWeight: 800 }}>AP</div>
      {!small && (
        <div style={{ lineHeight: 1.2 }}>
          <b>{t("appName")}</b>
          <div style={{ fontSize: 12, color: palette.navFg }}>{t("appSub")}</div>
        </div>
      )}
    </div>
  );
  const menu = (
    <Menu
      theme="dark"
      mode="inline"
      selectedKeys={sel ? [sel] : []}
      items={items}
      onClick={(e) => {
        nav(e.key);
        setDrawer(false);
      }}
    />
  );

  // versione dell'applicativo (AUD-F7.1), piccola in fondo al menu: «v0.4.0 · 940dc90», sul DEV «test · <commit>»
  const ver = useQuery({ queryKey: ["version"], queryFn: () => api.get<{ version: string; commit: string | null }>("/version"), staleTime: Infinity });
  const versionLabel = ver.data ? [ver.data.version, ver.data.commit].filter(Boolean).join(" · ") : "";
  const versionLine = (short: boolean) =>
    versionLabel ? (
      <div title={versionLabel} style={{ padding: short ? "12px 0" : "12px 24px", textAlign: short ? "center" : "left", fontSize: 11, color: palette.navFg, opacity: 0.6, fontFamily: "var(--ap-mono)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
        {short ? ver.data!.version : versionLabel}
      </div>
    ) : null;

  return (
    <FeedbackShell>
    <Layout style={{ minHeight: "100vh" }}>
      {!narrow && (
        <Sider
          width={248}
          collapsible
          collapsed={collapsed}
          collapsedWidth={72}
          onCollapse={(c) => toggle(c)}
          style={{ position: "sticky", top: 0, height: "100vh", overflow: "auto", zIndex: 20 }}
        >
          <div style={{ display: "flex", flexDirection: "column", minHeight: "calc(100vh - 48px)" }}>
            {brand(collapsed)}
            {menu}
            <div style={{ marginTop: "auto" }}>{versionLine(collapsed)}</div>
          </div>
        </Sider>
      )}
      {narrow && (
        <Drawer
          open={drawer}
          onClose={() => setDrawer(false)}
          placement="left"
          width={280}
          closable={false}
          styles={{ body: { padding: 0, background: palette.nav }, header: { display: "none" } }}
        >
          {brand(false)}
          {menu}
          {versionLine(false)}
        </Drawer>
      )}
      <Layout>
        <Header
          style={{
            display: "flex",
            alignItems: "center",
            gap: narrow ? 8 : 16,
            padding: narrow ? "0 12px" : "0 20px",
            borderBottom: `1px solid ${palette.hair}`,
            position: "sticky",
            top: 0,
            zIndex: 15,
          }}
        >
          {narrow && <Button type="text" icon={<MenuOutlined />} aria-label="Menu" onClick={() => setDrawer(true)} />}
          <div style={{ flex: 1, minWidth: 0, maxWidth: 520 }}>
            <GlobalSearch />
          </div>
          <Space size={narrow ? 6 : 8} style={{ marginLeft: "auto" }}>
            <Segmented
              size="small"
              value={i18n.language === "en" ? "en" : "it"}
              options={[
                { label: "IT", value: "it" },
                { label: "EN", value: "en" },
              ]}
              onChange={(v) => setLang(v as "it" | "en")}
            />
            <Dropdown
              menu={{
                items: [
                  { key: "security", icon: <SafetyCertificateOutlined />, label: t("c_menuSecurity") },
                  { key: "logout", icon: <LogoutOutlined />, label: t("logout") },
                ],
                onClick: async ({ key }) => {
                  if (key === "security") {
                    nav("/security");
                    return;
                  }
                  await logout();
                  nav("/login");
                },
              }}
            >
              {narrow ? (
                <Avatar style={{ background: palette.primary, cursor: "pointer" }}>{me.name.split(/\s+/).slice(0, 2).map((x) => x[0]).join("").toUpperCase()}</Avatar>
              ) : (
                <Button type="text" style={{ height: 44 }}>
                  <div style={{ textAlign: "left", lineHeight: 1.2 }}>
                    <Typography.Text strong>{me.name}</Typography.Text>
                    <div style={{ fontSize: 12, color: palette.muted }}>{t(`role_${me.role}`)}</div>
                  </div>
                </Button>
              )}
            </Dropdown>
          </Space>
        </Header>
        <Content style={{ padding: narrow ? 12 : 24, minWidth: 0 }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
    </FeedbackShell>
  );
}
